import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@/components/forge/Icon'
import { cn } from '@/lib/cn'
import { RcaView } from './RcaView'
import { TimeseriesView } from './TimeseriesView'
import { ReasoningView } from './ReasoningView'

/**
 * Anomaly · Analysis.
 *
 * Three views of one investigation: what the engine concluded, the signal it
 * concluded it from, and the record of it concluding.
 *
 * ── This tab was entirely hardcoded, and now none of it is ───────────────
 * It held four views transcribed from the design — a causal chain with an
 * invented 92% confidence, two literal sensor traces, a cross-sensor overlay and
 * a four-stage reasoning pipeline. None of it was joined to the plant, and the
 * figures were the part most likely to be believed.
 *
 * Every view here reads the record:
 *
 *   RCA        `content` — the analysis the engine wrote. This was the
 *              AI Synopsis tab, which held the same subject one tab away from a
 *              demo version of itself.
 *   Timeseries `sensor_tags`, and the readings endpoint behind each.
 *   Reasoning  `history` — what changed, when, and why the engine says so.
 *
 * ── The correlative view is gone and is not replaced ─────────────────────
 * It drew a normalised overlay of every sensor on one axis with a cross-sensor
 * correlation matrix beside it. Nothing serves a correlation: the figures in the
 * design were literals, and the ones in this payload are prose inside a root
 * cause's description ("-0.96 during, -0.01 normally"), written by the engine for
 * a human to read rather than for a chart to plot. Computing one in the browser
 * from the readings would be this screen producing a statistic the analysis did
 * not make, over a window it does not control the sampling of.
 *
 * ── Why the rail is vertical ──────────────────────────────────────────────
 * These three are peers, they are switched between repeatedly while reading, and
 * two of them are tall. A horizontal strip above tall content puts the switch
 * off-screen the moment somebody scrolls into the thing they came to look at.
 */

const VIEWS = [
  { id: 'rca', icon: 'search' },
  { id: 'timeseries', icon: 'gauge' },
  { id: 'reasoning', icon: 'ai' },
]

export function AnalysisTab({ anomaly }) {
  const { t } = useTranslation(['app', 'common'])
  const [view, setView] = useState('rca')

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <nav
        aria-label={t('anomalies.analysis.railLabel')}
        className="flex shrink-0 gap-1 overflow-x-auto lg:w-40 lg:flex-col"
      >
        {VIEWS.map(({ id, icon }) => {
          const on = view === id
          return (
            <button
              key={id}
              type="button"
              aria-current={on ? 'page' : undefined}
              onClick={() => setView(id)}
              className={cn(
                'flex items-center gap-2.5 rounded-sm border-s-2 px-3 py-2.5 text-body-sm whitespace-nowrap transition-colors',
                on
                  ? 'border-s-forge bg-sunken font-semibold text-ink'
                  : 'border-s-transparent text-ink-dim hover:text-ink',
              )}
            >
              <Icon name={icon} size="sm" className={on ? 'text-forge' : 'text-ink-mute'} />
              {t(`anomalies.analysis.view.${id}`)}
            </button>
          )
        })}
      </nav>

      <div className="min-w-0 flex-1">
        {view === 'rca' ? <RcaView anomaly={anomaly} /> : null}
        {view === 'timeseries' ? <TimeseriesView anomaly={anomaly} /> : null}
        {view === 'reasoning' ? <ReasoningView anomaly={anomaly} /> : null}
      </div>
    </div>
  )
}
